
// ? Axios instance
import { api } from "../../../services/apis/configs/axiosConfigs";

// ? Constants
import { API_URI } from "../../../constants/consts";

// ? Auxiliary types for relationships
import {
  type Reviewer,
  type GetReviewersResponse,
  type CreateReviewerInput,
  type ReviewerDetails,
} from "./types/types";

// * Importing the existing Pagination type
import { type Pagination } from "../../../services/stores/types";

export const reviewersAPI = {
  /**
   * ? Fetches all Reviewers for a specific application with search and pagination support.
   *
   * @param applicationId - Application ID.
   * @param search - Optional search term.
   * @param page - Page number for pagination.
   * @param token - Authentication token.
   * @param perPage - Number of items per page.
   * @returns A promise that resolves to GetReviewersResponse.
   */
  getAll: async function (
    applicationId: string,
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<GetReviewersResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/applications/${applicationId}/reviewers?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination || {
          currentPage: 0,
          totalPages: 0,
          itemsPerPage: 0,
          totalItems: 0,
        };

        return {
          reviewers: data.data || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching application reviewers:", error);
        throw new Error("Failed to fetch application reviewers");
      });
  },

  /**
   * TODO: Fetches a specific Reviewer by ID within an application.
   *
   * @param applicationId - Application ID.
   * @param id - Reviewer ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to ReviewerDetails.
   */
  getById: async function (
    applicationId: string,
    id: string,
    token: string
  ): Promise<ReviewerDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/reviewers/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching application reviewer:", error);
        throw new Error("Failed to fetch application reviewer");
      });
  },

  /**
   * ! Creates a new Reviewer within a specific application.
   *
   * @param applicationId - Application ID.
   * @param data - Data to create the Reviewer.
   * @param token - Authentication token.
   * @returns A promise that resolves to Reviewer.
   */
  create: async function (
    applicationId: string,
    data: CreateReviewerInput,
    token: string
  ): Promise<Reviewer> {
    const payload = {
      ...data,
      application_id: applicationId,
    };

    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/reviewers`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating application reviewer:", error, data);
        throw error;
      });
  },

  /**
   * ! Updates an existing Reviewer within a specific application.
   *
   * @param applicationId - Application ID.
   * @param id - Reviewer ID.
   * @param data - Data to update the Reviewer.
   * @param token - Authentication token.
   * @returns A promise that resolves to Reviewer.
   */
  update: async function (
    applicationId: string,
    id: string,
    data: CreateReviewerInput,
    token: string
  ): Promise<Reviewer> {
    const payload = {
      ...data,
      application_id: applicationId,
    };

    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/reviewers/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating application reviewer:", error);
        throw new Error("Failed to update application reviewer");
      });
  },

  /**
   * ? Deletes a specific Reviewer within an application.
   *
   * @param applicationId - Application ID.
   * @param id - Reviewer ID.
   * @param token - Authentication token.
   * @returns A promise that resolves to void.
   */
  delete: async function (
    applicationId: string,
    id: string,
    token: string
  ): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/reviewers/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting application reviewer:", error);
        throw new Error("Failed to delete application reviewer");
      });
  },
};
